import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, Home, TrendingUp } from 'lucide-react';
import { Button } from '@/shared/ui/atoms/Button';
import { Badge } from '@/shared/ui/atoms/Badge';
import { ThemeToggle } from '@/shared/ui/molecules/ThemeToggle';

/**
 * Minimalist and accessible 404 Not Found Page.
 *
 * Provides a clean interface when a user navigates to a non-existent URL,
 * with quick recovery paths to return to previous page or dashboard.
 */
export default function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[100dvh] w-full flex-col bg-background text-foreground antialiased selection:bg-primary selection:text-primary-foreground pb-safe">
      {/* Top Navbar */}
      <header className="sticky top-0 z-30 flex flex-col w-full border-b border-border/80 bg-background/80 pt-safe backdrop-blur-md transition-colors">
        <div className="flex h-16 w-full items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-2.5 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg px-1 py-0.5 transition-opacity hover:opacity-90"
            aria-label="Cartera"
          >
            <div className="flex size-8 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-xs transition-transform group-hover:scale-105">
              <TrendingUp className="size-4.5 stroke-[2.2]" />
            </div>
            <span className="font-display text-base sm:text-lg font-bold tracking-tight text-foreground">
              Cartera
            </span>
          </Link>
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-center">
            <Badge
              variant="outline"
              className="px-3 py-1 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground border-border/80 bg-muted/30"
            >
              Error 404
            </Badge>
          </div>

          {/* Minimal Icon Glyph */}
          <div className="relative flex size-20 items-center justify-center rounded-2xl border border-border/60 bg-muted/40 text-muted-foreground shadow-xs transition-colors">
            <Compass className="size-10 stroke-[1.5] text-foreground/80" />
          </div>

          {/* Heading and Description */}
          <div className="space-y-2">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Página no encontrada
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              La página que buscas no existe, ha sido movida o la dirección ingresada es incorrecta.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 w-full pt-2">
            <Button
              variant="outline"
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/dashboard');
                }
              }}
              className="gap-2 cursor-pointer order-2 sm:order-1"
            >
              <ArrowLeft className="size-4" />
              Volver atrás
            </Button>
            <Button asChild className="gap-2 cursor-pointer order-1 sm:order-2">
              <Link to="/dashboard">
                <Home className="size-4" />
                Ir al Dashboard
              </Link>
            </Button>
          </div>

          {/* Quick Login / Home Link */}
          <div className="pt-4 border-t border-border/40 w-full">
            <p className="text-xs text-muted-foreground">
              ¿Problemas para acceder?{' '}
              <Link
                to="/login"
                className="text-foreground underline underline-offset-4 hover:text-primary transition-colors font-medium"
              >
                Ir a Iniciar Sesión
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-muted-foreground border-t border-border/40">
        Cartera &copy; {new Date().getFullYear()}
      </footer>
    </div>
  );
}
